import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ListarPecas.css';
import api from '../../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { TableCode } from '../ui/Table';
import {
  ProductContextError,
  codigoProdutoDaQuery,
  produtoSelecionadoDaFormalizacao,
  produtoSelecionadoDeProduto,
} from '../../utils/productContext';

function ListarPecas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const codigoProduto = codigoProdutoDaQuery(searchParams);
  const setCodigoProduto = (valor) => {
    setSearchParams(valor ? { produto: valor } : {}, { replace: true });
  };
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [modoProvisorio, setModoProvisorio] = useState(false);
  const [produtosProvisorios, setProdutosProvisorios] = useState([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [novoCodigo81, setNovoCodigo81] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [familiaFormalizacao, setFamiliaFormalizacao] = useState('');
  const [preview, setPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingFormalizar, setLoadingFormalizar] = useState(false);
  const [erroFormalizacao, setErroFormalizacao] = useState(null);
  const [sucessoFormalizacao, setSucessoFormalizacao] = useState(null);

  const buscarProduto = async (codigo = codigoProduto) => {
    if (!codigo) {
      setErro('Digite o código do produto');
      return;
    }
    setLoading(true);
    setErro(null);
    setProdutoSelecionado(null);
    setModoProvisorio(false);
    setProdutosProvisorios([]);
    setPreview([]);
    setErroFormalizacao(null);
    setSucessoFormalizacao(null);
    try {
      const response = await api.get(`/api/produtos/by-code/${encodeURIComponent(codigo)}`);
      const produto = produtoSelecionadoDeProduto(response.data);
      setProdutoSelecionado(produto);
      if (produto.is_provisional) setNovoNome(produto.nome || '');
    } catch (error) {
      setErro(error.response?.status === 404
        ? 'Produto não encontrado'
        : error instanceof ProductContextError
          ? error.message
          : error.response?.data?.detail || 'Erro ao buscar produto');
    } finally {
      setLoading(false);
    }
  };

  // A query string existente continua abrindo a seleção pelo código.
  useEffect(() => {
    if (codigoProduto) buscarProduto(codigoProduto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscarProvisórios = async () => {
    setLoading(true);
    setErro(null);
    setProdutoSelecionado(null);
    setSucessoFormalizacao(null);
    try {
      const response = await api.get('/pecas/provisorios');
      setModoProvisorio(true);
      setProdutosProvisorios(response.data);
    } catch (error) {
      setErro(error.response?.data?.detail || 'Erro ao buscar provisórios');
    } finally {
      setLoading(false);
    }
  };

  const selecionarProdutoProvisorio = (produto) => {
    try {
      const contexto = produtoSelecionadoDeProduto(produto);
      setCodigoProduto(contexto.codigo);
      setProdutoSelecionado(contexto);
      setNovoNome(contexto.nome || '');
      setModoProvisorio(false);
      setProdutosProvisorios([]);
      setPreview([]);
      setErroFormalizacao(null);
      setSucessoFormalizacao(null);
      setErro(null);
    } catch (error) {
      setErro(error.message);
    }
  };

  const handleVisualizarPreview = async () => {
    if (!familiaFormalizacao) return;
    setLoadingPreview(true);
    setErroFormalizacao(null);
    try {
      const response = await api.get(
        `/pecas/produto/${codigoProduto}/preview-formalizacao`,
        { params: { familia: familiaFormalizacao } }
      );
      setPreview(response.data);
    } catch (error) {
      setErroFormalizacao(error.response?.data?.detail || 'Erro ao gerar preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const moverPeca = (index, direcao) => {
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= preview.length) return;
    const nova = [...preview];
    [nova[index], nova[alvo]] = [nova[alvo], nova[index]];
    setPreview(nova.map((item, i) => ({
      ...item,
      codigo_novo: familiaFormalizacao + String(i + 1).padStart(3, '0'),
    })));
  };

  const handleFormalizar = async () => {
    if (!novoCodigo81 || !novoNome || !familiaFormalizacao) {
      setErroFormalizacao('Preencha todos os campos obrigatórios');
      return;
    }
    if (novoCodigo81.length !== 9) {
      setErroFormalizacao('O código 81 deve ter exatamente 9 dígitos');
      return;
    }
    if (familiaFormalizacao.length !== 6) {
      setErroFormalizacao('A família deve ter exatamente 6 dígitos');
      return;
    }
    setLoadingFormalizar(true);
    setErroFormalizacao(null);
    setSucessoFormalizacao(null);
    try {
      const response = await api.post(`/pecas/produto/${codigoProduto}/formalizar`, {
        codigo_81: novoCodigo81,
        nome: novoNome,
        familia: familiaFormalizacao,
        ordem_pecas: preview.length > 0 ? preview.map(item => item.codigo_atual) : undefined,
      });
      const formalizado = produtoSelecionadoDaFormalizacao(response.data, produtoSelecionado);
      setProdutoSelecionado(formalizado);
      setCodigoProduto(formalizado.codigo);
      setPreview([]);
      setSucessoFormalizacao('Produto formalizado com sucesso!');
    } catch (error) {
      console.error('formalizar produto:', error);
      setErroFormalizacao(error instanceof ProductContextError
        ? error.message
        : error.response?.data?.detail || 'Erro ao formalizar');
    } finally {
      setLoadingFormalizar(false);
    }
  };

  const handleCodigoProdutoChange = (valor) => {
    setCodigoProduto(valor);
    setProdutoSelecionado(null);
    setProdutosProvisorios([]);
    setModoProvisorio(false);
    setErro(null);
    setSucessoFormalizacao(null);
  };

  return (
    <div className="listar-pecas">
      <div className="listar-container">
        <h1 className="cw-page-title">Produtos</h1>
        <p className="cw-page-subtitle">Busque e gerencie produtos em desenvolvimento.</p>

        <Card className="busca-container">
          <input
            type="text"
            inputMode="numeric"
            maxLength={9}
            value={codigoProduto}
            onChange={(e) => handleCodigoProdutoChange(
              e.target.value.replace(/\D/g, '').slice(0, 9)
            )}
            onKeyPress={(e) => { if (e.key === 'Enter') buscarProduto(); }}
            placeholder="Digite o código do produto (ex: 810015098)"
            className="input-busca"
          />
          <Button onClick={() => buscarProduto()} loading={loading && !modoProvisorio}>Buscar</Button>
          <Button
            variant="secondary"
            onClick={buscarProvisórios}
            className={modoProvisorio ? 'btn-provisorios-ativo' : ''}
            disabled={loading}
          >
            Em desenvolvimento
          </Button>
        </Card>

        {erro && <div className="alerta erro" role="alert">{erro}</div>}
        {sucessoFormalizacao && <div className="form-alerta sucesso">{sucessoFormalizacao}</div>}
        {loading && <p className="cw-page-subtitle" role="status">Buscando produto...</p>}
        {!loading && !erro && !produtoSelecionado && !modoProvisorio && (
          <p className="cw-page-subtitle">Busque por código ou escolha um produto em desenvolvimento.</p>
        )}

        {modoProvisorio && (
          <Card className="pecas-lista">
            <div className="pecas-header">
              <h2 className="cw-section-title">{produtosProvisorios.length} produto(s) em desenvolvimento</h2>
            </div>
            {produtosProvisorios.length === 0 && (
              <p className="lista-vazia">Nenhum produto provisório encontrado.</p>
            )}
            {produtosProvisorios.map((prod) => (
              <div key={prod.id} className="peca-card peca-card-provisorio">
                <div className="peca-info">
                  <div className="peca-codigo">{prod.codigo}<span className="badge-provisorio">PROVISÓRIO</span></div>
                  <div className="peca-nome">{prod.nome}</div>
                  {prod.programa && <div className="peca-familia">Programa: {prod.programa}</div>}
                  {prod.alternativa && <div className="peca-familia">Alternativa: {prod.alternativa}</div>}
                </div>
                <div className="peca-acoes">
                  {Number.isInteger(prod.id) && prod.id > 0 && (
                    <Button variant="secondary" onClick={() => navigate(`/produtos/${prod.id}`)}>Abrir Produto</Button>
                  )}
                  <Button variant="secondary" onClick={() => selecionarProdutoProvisorio(prod)}>Formalizar</Button>
                </div>
              </div>
            ))}
          </Card>
        )}

        {produtoSelecionado && (
          <Card className="produto-result-card">
            <div>
              <h2 className="cw-section-title">{produtoSelecionado.nome || 'Produto sem nome'}</h2>
              <div className="produto-result-meta">
                <TableCode>{produtoSelecionado.codigo}</TableCode>
                <Badge variant={produtoSelecionado.is_provisional ? 'warning' : 'success'} dot>
                  {produtoSelecionado.is_provisional ? 'Provisório' : 'Formal'}
                </Badge>
              </div>
            </div>
            <Button onClick={() => navigate(`/produtos/${produtoSelecionado.id}`)}>Abrir Produto</Button>
          </Card>
        )}

        {/* Painel de formalização */}
        {produtoSelecionado?.is_provisional && (
          <div className="painel-formalizacao">
            <div className="painel-formalizacao-titulo">
              Produto Provisório — Formalizar
            </div>

            {erroFormalizacao && (
              <div className="form-alerta erro">{erroFormalizacao}</div>
            )}
            {sucessoFormalizacao && (
              <div className="form-alerta sucesso">{sucessoFormalizacao}</div>
            )}

            <div className="form-row-3">
              <div className="form-field">
                <label>Novo código 81 * <small>(9 dígitos)</small></label>
                <input
                  type="text"
                  value={novoCodigo81}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                    setNovoCodigo81(val);
                    setErroFormalizacao(null);
                  }}
                  placeholder="Ex: 810015098"
                  maxLength={9}
                  style={novoCodigo81 && novoCodigo81.length !== 9 ? { borderColor: '#e53e3e' } : {}}
                />
                {novoCodigo81 && novoCodigo81.length !== 9 && (
                  <small style={{ color: '#e53e3e' }}>{novoCodigo81.length}/9 dígitos</small>
                )}
              </div>
              <div className="form-field">
                <label>Nome do produto *</label>
                <input
                  type="text"
                  value={novoNome}
                  onChange={(e) => setNovoNome(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Família das peças * <small>(6 dígitos)</small></label>
                <div className="familia-row">
                  <input
                    type="text"
                    value={familiaFormalizacao}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setFamiliaFormalizacao(val);
                      setPreview([]);
                      setErroFormalizacao(null);
                    }}
                    placeholder="Ex: 510001"
                    maxLength={6}
                    style={familiaFormalizacao && familiaFormalizacao.length !== 6 ? { borderColor: '#e53e3e' } : {}}
                  />
                  <button
                    className="btn-preview"
                    onClick={handleVisualizarPreview}
                    disabled={familiaFormalizacao.length !== 6 || loadingPreview}
                  >
                    {loadingPreview ? '' : 'Preview'}
                  </button>
                </div>
                {familiaFormalizacao && familiaFormalizacao.length !== 6 && (
                  <small style={{ color: '#e53e3e' }}>{familiaFormalizacao.length}/6 dígitos</small>
                )}
              </div>
            </div>

            {preview.length > 0 && (
              <table className="tabela-preview">
                <thead>
                  <tr>
                    <th>Ordem</th>
                    <th>Código atual</th>
                    <th>Código novo</th>
                    <th>Nome</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((item, i) => (
                    <tr key={item.codigo_atual}>
                      <td className="preview-ordem-col">
                        <button
                          className="btn-ordem"
                          onClick={() => moverPeca(i, -1)}
                          disabled={i === 0}
                          title="Mover para cima"
                        >▲</button>
                        <button
                          className="btn-ordem"
                          onClick={() => moverPeca(i, 1)}
                          disabled={i === preview.length - 1}
                          title="Mover para baixo"
                        >▼</button>
                      </td>
                      <td>{item.codigo_atual}</td>
                      <td>{item.codigo_novo}</td>
                      <td>{item.nome_peca}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="painel-formalizacao-actions">
              <button
                className="btn-formalizar"
                onClick={handleFormalizar}
                disabled={loadingFormalizar}
              >
                {loadingFormalizar ? ' Formalizando...' : 'Formalizar Produto'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default ListarPecas;
